/** One activity bar on the WBS board: the colored/grey pill with its code+name,
 *  carried/locked glyphs, resize lip, and (when editing) the BarPopover. Pure
 *  presentation off the parent's drag/selection state — every interaction is a
 *  callback prop so the layout file stays an orchestrator. */
import React from "react";
import type { DepGraphPredType, ExistingWorkCode, ScopeMember } from "../../../../models";
import type { DepCandidate } from "../../depCore/DepChipTypeahead/DepChipTypeahead";
import type { Bar, LeafRow } from "../boardLayout";
import { autoFontColor } from "../../../../utils/color";
import { hashColor } from "../../PullPlanGrid/helpers";
import { isTrainCarId } from "../../PullPlanGrid/train/trainReconcile";
import BarPopover from "../BarPopover/BarPopover";

type DrivingPred = {
  code: string;
  color: string;
  name: string;
  rel: string;
  predId: string;
  type: DepGraphPredType;
  lagDays: number;
};

interface Props {
  bar: Bar;
  leaf: LeafRow;
  dayPx: number;
  laneHeight: number;
  workCodeColors: Record<string, string>;
  anchorDir: Map<number, "in" | "out" | "both">;
  selectedIds: Set<number>;
  editingId: number | null;
  inDrag: boolean;
  highlightCode?: string | null;
  trainMappedRefs?: Set<number>;
  previewRefs?: Set<number>;
  codeDisplay?: Record<string, string>;
  codeNameByKey: Map<string, string>;
  editable: (m: ScopeMember) => boolean;
  barTip?: (m: ScopeMember) => string | undefined;
  dragMoveOffset: (id: number) => number;
  dragResizeExtra: (id: number) => number;
  scheduleId?: number;
  paletteCodes?: ExistingWorkCode[];
  editAnchorRef: React.MutableRefObject<{ left: number; top: number }>;
  setEditingId: React.Dispatch<React.SetStateAction<number | null>>;
  onStartBarDrag: (e: React.MouseEvent, bar: Bar) => void;
  onStartResize: (e: React.MouseEvent, bar: Bar) => void;
  onHoverBar?: (m: ScopeMember | null) => void;
  onSelectionChange?: (next: Set<number>) => void;
  onRenameBar?: (activityId: number, name: string) => void;
  onResizeBar?: (activityId: number, durationDays: number) => void;
  onRecodeBar?: (
    activityId: number,
    draft: { code: string; name: string; color: string },
  ) => void;
  onRemoveNew?: (activityId: number) => void;
  onDeleteBar?: (activityId: number) => void;
  depCandidates?: DepCandidate[];
  drivingPredByBarId?: Map<number, DrivingPred>;
  onSetPredEdge?: (
    barId: number,
    currentPredId: string | null,
    newPredId: string | null,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  onRemovePredEdge?: (barId: number, predId: string) => void;
  trainDepCandidates?: DepCandidate[];
  trainDrivingPredByBar?: Map<number, DrivingPred>;
  onSetTrainPred?: (
    barId: number,
    currentPredId: string | null,
    newPredId: string | null,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  onRemoveTrainPred?: (barId: number, predId: string) => void;
  barGraphId?: (activityId: number) => string;
}

const BoardBar: React.FC<Props> = ({
  bar,
  leaf,
  dayPx,
  laneHeight,
  workCodeColors,
  anchorDir,
  selectedIds,
  editingId,
  inDrag,
  highlightCode,
  trainMappedRefs,
  previewRefs,
  codeDisplay,
  codeNameByKey,
  editable,
  barTip,
  dragMoveOffset,
  dragResizeExtra,
  scheduleId,
  paletteCodes,
  editAnchorRef,
  setEditingId,
  onStartBarDrag,
  onStartResize,
  onHoverBar,
  onSelectionChange,
  onRenameBar,
  onResizeBar,
  onRecodeBar,
  onRemoveNew,
  onDeleteBar,
  depCandidates,
  drivingPredByBarId,
  onSetPredEdge,
  onRemovePredEdge,
  trainDepCandidates,
  trainDrivingPredByBar,
  onSetTrainPred,
  onRemoveTrainPred,
  barGraphId,
}) => {
  const m = bar.member;
  const takted = Boolean(m.takt_work);
  const color = takted
    ? workCodeColors[m.takt_work] ?? hashColor(m.takt_work)
    : "";
  const anchor = anchorDir.get(m.activity_id);
  const done = m.status === "TK_Complete";
  const isNew = m.activity_id < 0 && !isTrainCarId(m.activity_id);
  const canEdit = editable(m);
  const selected = selectedIds.has(m.activity_id);
  const isEditing = editingId === m.activity_id;
  return (
    <div
      data-wbs-bar={m.activity_id}
      data-wbs-editable={canEdit ? "1" : "0"}
      className={
        "wbs-board-bar" +
        (takted ? "" : " wbs-board-bar--untakted") +
        (anchor ? " wbs-board-bar--anchor" : "") +
        (done ? " wbs-board-bar--done" : "") +
        (trainMappedRefs?.has(m.activity_id)
          ? " wbs-board-bar--train"
          : "") +
        (previewRefs?.has(m.activity_id)
          ? " wbs-board-bar--preview"
          : "") +
        (selected ? " wbs-board-bar--selected" : "") +
        (isEditing ? " wbs-board-bar--editing" : "") +
        (highlightCode && m.takt_work === highlightCode
          ? " wbs-board-bar--hl"
          : "") +
        (inDrag ? " wbs-board-bar--in-drag" : "") +
        (canEdit ? " wbs-board-bar--editable" : "")
      }
      style={{
        left: bar.startIndex * dayPx + dragMoveOffset(m.activity_id),
        width: Math.max(
          dayPx,
          bar.durationDays * dayPx + dragResizeExtra(m.activity_id),
        ),
        top: 4 + bar.lane * laneHeight,
        height: laneHeight - 4,
        ...(takted
          ? { background: color, color: autoFontColor(color) }
          : {}),
      }}
      title={
        barTip?.(m) ??
        `${m.task_code} · ${m.task_name}${
          m.takt_work ? ` · « ${m.takt_work} »` : " · untakted"
        }${anchor ? ` · anchor ${anchor}` : ""}`
      }
      onMouseDown={(e) => onStartBarDrag(e, bar)}
      onMouseEnter={() => onHoverBar?.(m)}
      onMouseLeave={() => onHoverBar?.(null)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!canEdit) return;
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        editAnchorRef.current = { left: r.left, top: r.bottom + 6 };
        setEditingId(m.activity_id);
      }}
    >
      <span className="wbs-board-bar-text">
        {done && bar.durationDays * dayPx >= 40 && (
          <span className="wbs-board-bar-lock" title="Completed — locked" aria-hidden="true">🔒</span>
        )}
        {!done && !isNew && bar.durationDays * dayPx >= 40 && (
          <span className="wbs-board-bar-carried" title="Carried over from the previous schedule" aria-hidden="true">⟲</span>
        )}
        {takted ? (
          bar.durationDays * dayPx < 56 ? (
            <b className="wbs-board-bar-code">{codeDisplay?.[m.takt_work] ?? m.takt_work}</b>
          ) : (
            <>
              <b className="wbs-board-bar-code">{codeDisplay?.[m.takt_work] ?? m.takt_work}</b>
              <span className="wbs-board-bar-name">{codeNameByKey.get(m.takt_work) ?? m.task_name}</span>
            </>
          )
        ) : (
          <span className="wbs-board-bar-name">{m.task_name}</span>
        )}
      </span>
      {canEdit && <div className="wbs-board-resize" onMouseDown={(e) => onStartResize(e, bar)} />}
      {isEditing && (
        <BarPopover
          name={m.task_name}
          durationDays={bar.durationDays}
          code={m.takt_work}
          scheduleId={scheduleId ?? 0}
          paletteCodes={paletteCodes ?? []}
          drivingPred={
            isTrainCarId(m.activity_id)
              ? trainDrivingPredByBar?.get(m.activity_id) ?? null
              : drivingPredByBarId?.get(m.activity_id) ?? null
          }
          predCandidates={
            isTrainCarId(m.activity_id)
              ? onSetTrainPred && trainDepCandidates
                ? trainDepCandidates.filter(
                    (c) => c.instanceId !== String(m.activity_id),
                  )
                : undefined
              : onSetPredEdge && depCandidates
                ? depCandidates.filter(
                    (c) => c.instanceId !== barGraphId?.(m.activity_id),
                  )
                : undefined
          }
          onSetPred={
            isTrainCarId(m.activity_id)
              ? onSetTrainPred
                ? (newPredId, type, lagDays) =>
                    onSetTrainPred(
                      m.activity_id,
                      trainDrivingPredByBar?.get(m.activity_id)?.predId ?? null,
                      newPredId,
                      type,
                      lagDays,
                    )
                : undefined
              : onSetPredEdge
                ? (newPredId, type, lagDays) =>
                    onSetPredEdge(
                      m.activity_id,
                      drivingPredByBarId?.get(m.activity_id)?.predId ?? null,
                      newPredId,
                      type,
                      lagDays,
                    )
                : undefined
          }
          onRemovePred={
            isTrainCarId(m.activity_id)
              ? onRemoveTrainPred && trainDrivingPredByBar?.get(m.activity_id)
                ? () =>
                    onRemoveTrainPred(
                      m.activity_id,
                      trainDrivingPredByBar.get(m.activity_id)!.predId,
                    )
                : undefined
              : onRemovePredEdge && drivingPredByBarId?.get(m.activity_id)
                ? () =>
                    onRemovePredEdge(
                      m.activity_id,
                      drivingPredByBarId.get(m.activity_id)!.predId,
                    )
                : undefined
          }
          anchor={editAnchorRef.current}
          onSelectFromHere={
            onSelectionChange
              ? () => {
                  // This LEVEL only: the bar + everything in its
                  // row starting on/after it.
                  const from = bar.startIndex;
                  const ids = new Set<number>();
                  for (const b of leaf.bars) {
                    if (b.startIndex >= from && editable(b.member)) {
                      ids.add(b.member.activity_id);
                    }
                  }
                  onSelectionChange(ids);
                }
              : undefined
          }
          onApply={(patch) => {
            if (patch.name != null) onRenameBar?.(m.activity_id, patch.name);
            if (patch.durationDays != null)
              onResizeBar?.(m.activity_id, patch.durationDays);
            if (patch.code != null) onRecodeBar?.(m.activity_id, patch.code);
          }}
          onDelete={() => {
            setEditingId(null);
            if (isNew) onRemoveNew?.(m.activity_id);
            else onDeleteBar?.(m.activity_id);
          }}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
};

export default BoardBar;
